'use client';

import { Box as ChakraBox } from '@chakra-ui/react';
import type { BoxProps as ChakraBoxProps } from '@chakra-ui/react';
import { forwardRef } from 'react';

import { filterPropsForElementWithAs } from '../lib/prop-filters';

export interface BoxProps
  extends Omit<
    ChakraBoxProps,
    // Chakra v2 deprecated props that should not be used
    | 'bgColor'
    | 'w'
    | 'h'
    | 'minW'
    | 'maxW'
    | 'minH'
    | 'maxH'
    | 'p'
    | 'pt'
    | 'pr'
    | 'pb'
    | 'pl'
    | 'px'
    | 'py'
    | 'm'
    | 'mt'
    | 'mr'
    | 'mb'
    | 'ml'
    | 'mx'
    | 'my'
  > {
  // Test ID for testing - converted to data-testid attribute
  // Both testId and data-testid are supported for flexibility
  testId?: string;
  'data-testid'?: string; // Native HTML attribute
  // Enforce full prop names only
  width?: ChakraBoxProps['width'];
  height?: ChakraBoxProps['height'];
  minWidth?: ChakraBoxProps['minWidth'];
  maxWidth?: ChakraBoxProps['maxWidth'];
  minHeight?: ChakraBoxProps['minHeight'];
  maxHeight?: ChakraBoxProps['maxHeight'];
  padding?: ChakraBoxProps['padding'];
  paddingTop?: ChakraBoxProps['paddingTop'];
  paddingRight?: ChakraBoxProps['paddingRight'];
  paddingBottom?: ChakraBoxProps['paddingBottom'];
  paddingLeft?: ChakraBoxProps['paddingLeft'];
  paddingX?: ChakraBoxProps['paddingX'];
  paddingY?: ChakraBoxProps['paddingY'];
  margin?: ChakraBoxProps['margin'];
  marginTop?: ChakraBoxProps['marginTop'];
  marginRight?: ChakraBoxProps['marginRight'];
  marginBottom?: ChakraBoxProps['marginBottom'];
  marginLeft?: ChakraBoxProps['marginLeft'];
  marginX?: ChakraBoxProps['marginX'];
  marginY?: ChakraBoxProps['marginY'];
  backgroundColor?: ChakraBoxProps['backgroundColor'];
}

/**
 * Box is the most fundamental layout component in TMS UI.
 * It renders a div element with style props.
 * When using the `as` prop with native HTML elements, Chakra-specific props are automatically filtered.
 *
 * @example
 * <Box padding={4} backgroundColor="#E6F0FF">
 *   Content
 * </Box>
 *
 * @example
 * <Box as="hr" border="1px solid" borderColor="gray.200">
 *   This will automatically filter out borderWidth and borderColor
 * </Box>
 */
export const Box = forwardRef<HTMLDivElement, BoxProps>((props, ref) => {
  const { as: element, 'data-testid': dataTestid, testId: chakraTestId, ...restProps } = props;

  // Enhanced prop filtering with defensive programming
  let filteredProps = restProps;

  if (element) {
    // Handle both string and component types safely
    const elementType =
      typeof element === 'string' ? element : element.displayName || element.name || 'component';

    // Apply filtering for native HTML elements while preserving the 'as' prop
    filteredProps = filterPropsForElementWithAs(restProps, elementType, element);
  }

  // Handle testid: use data-testid attribute (native HTML, Chakra UI v3 compatible)
  // If data-testid is provided, use it; otherwise use testId if provided
  const finalTestId = dataTestid ?? chakraTestId;

  // Build final props with data-testid (standard HTML attribute)
  const finalProps: Record<string, unknown> = {
    ...filteredProps,
    ...(finalTestId ? { 'data-testid': finalTestId } : {}),
  };

  return <ChakraBox ref={ref} as={element} {...finalProps} />;
});

Box.displayName = 'Box';
